/**
 * PBP Wishlist – localStorage + metafield-ready architecture
 * ----------------------------------------------------------
 * Storage: localStorage key "pbp_wishlist" => JSON array of product objects
 * Each item: { id, handle, title, url, image, price, priceFormatted, variantId, addedAt }
 *
 * Dispatches custom event "pbp:wishlist:change" on document whenever the list changes.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'pbp_wishlist';

  /* ── Heart SVG (used consistently everywhere) ─── */
  var HEART_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M16.5 3C14.76 3 13.09 3.81 12 5.09 10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';
  var HEART_SVG_FILLED = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M16.5 3C14.76 3 13.09 3.81 12 5.09 10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" stroke="currentColor" stroke-width="1.2" fill="currentColor"/></svg>';

  /* ── helpers ─────────────────────────────────────────── */
  function getList() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (_) {
      return [];
    }
  }

  function saveList(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('pbp:wishlist:change', { detail: { items: list } }));
  }

  function isInWishlist(productId) {
    return getList().some(function (item) { return String(item.id) === String(productId); });
  }

  function addItem(product) {
    var list = getList();
    if (list.some(function (i) { return String(i.id) === String(product.id); })) return list;
    product.addedAt = Date.now();
    list.push(product);
    saveList(list);
    return list;
  }

  function removeItem(productId) {
    var list = getList().filter(function (i) { return String(i.id) !== String(productId); });
    saveList(list);
    return list;
  }

  function toggleItem(product) {
    if (isInWishlist(product.id)) {
      removeItem(product.id);
      return false;
    } else {
      addItem(product);
      return true;
    }
  }

  function getCount() {
    return getList().length;
  }

  /* ── Add to Cart via Shopify AJAX ────────────────────── */
  function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity }] })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart add failed');
      return res.json();
    });
  }

  /* ── expose globally ─────────────────────────────────── */
  window.PBPWishlist = {
    getList: getList,
    addItem: addItem,
    removeItem: removeItem,
    toggleItem: toggleItem,
    isInWishlist: isInWishlist,
    getCount: getCount,
    addToCart: addToCart
  };

  /* ── heart button binding ────────────────────────────── */
  function initHeartButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');

      // Set initial state
      if (isInWishlist(pid)) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var product = {
          id: btn.getAttribute('data-product-id'),
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          url: btn.getAttribute('data-product-url'),
          image: btn.getAttribute('data-product-image'),
          price: btn.getAttribute('data-product-price'),
          priceFormatted: btn.getAttribute('data-product-price-formatted'),
          variantId: btn.getAttribute('data-variant-id')
        };

        var added = toggleItem(product);

        btn.classList.toggle('is-wishlisted', added);
        btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');

        // Animate heart
        btn.classList.add('is-animating');
        setTimeout(function () { btn.classList.remove('is-animating'); }, 400);

        // Open drawer if item was added
        if (added) {
          openDrawer();
        }
      });
    });
  }

  /* ── drawer logic ────────────────────────────────────── */
  var drawerEl = null;
  var overlayEl = null;

  function getDrawer() {
    if (!drawerEl) {
      drawerEl = document.getElementById('pbp-wishlist-drawer');
      overlayEl = document.getElementById('pbp-wishlist-overlay');
    }
    return drawerEl;
  }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    renderDrawerItems();
    drawer.classList.add('is-open');
    if (overlayEl) overlayEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlayEl) overlayEl.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function renderDrawerItems() {
    var drawer = getDrawer();
    if (!drawer) return;

    var list = getList();
    var body = drawer.querySelector('.pbp-wishlist-drawer__body');
    var counter = drawer.querySelector('.pbp-wishlist-drawer__count');

    if (counter) counter.textContent = list.length;

    if (!list.length) {
      body.innerHTML = '<div class="pbp-wishlist-drawer__empty">' +
        '<svg width="48" height="48" viewBox="0 0 24 24" fill="none"><path d="M16.5 3C14.76 3 13.09 3.81 12 5.09 10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" stroke="#d1d5db" stroke-width="1.2" fill="none"/></svg>' +
        '<p>Your wishlist is empty</p>' +
        '</div>';
      return;
    }

    var html = '';
    list.forEach(function (item) {
      var hasVariant = item.variantId && item.variantId !== 'null' && item.variantId !== '';
      html += '<div class="pbp-wishlist-drawer__item" data-item-id="' + item.id + '">' +
        '<a href="' + item.url + '" class="pbp-wishlist-drawer__itemImg">' +
          (item.image ? '<img src="' + item.image + '" alt="' + (item.title || '').replace(/"/g, '&quot;') + '" loading="lazy">' : '<div class="pbp-wishlist-drawer__placeholder"></div>') +
        '</a>' +
        '<div class="pbp-wishlist-drawer__itemInfo">' +
          '<a href="' + item.url + '" class="pbp-wishlist-drawer__itemTitle">' + item.title + '</a>' +
          '<div class="pbp-wishlist-drawer__itemPrice">' + (item.priceFormatted || '') + '</div>' +
          '<div class="pbp-wishlist-drawer__itemActions">' +
            (hasVariant
              ? '<button type="button" class="pbp-wishlist-drawer__addCart" data-add-variant="' + item.variantId + '">Add to Cart</button>'
              : '<a href="' + item.url + '" class="pbp-wishlist-drawer__addCart">View Product</a>') +
          '</div>' +
        '</div>' +
        '<button type="button" class="pbp-wishlist-drawer__remove" data-remove-id="' + item.id + '" aria-label="Remove ' + (item.title || '').replace(/"/g, '&quot;') + '">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>';
    });

    body.innerHTML = html;

    // Bind remove buttons
    body.querySelectorAll('.pbp-wishlist-drawer__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var rid = btn.getAttribute('data-remove-id');
        removeItem(rid);
        renderDrawerItems();
        syncHeartButtons();
      });
    });

    // Bind Add to Cart buttons
    body.querySelectorAll('[data-add-variant]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var vid = btn.getAttribute('data-add-variant');
        btn.textContent = 'Adding...';
        btn.disabled = true;
        addToCart(vid).then(function () {
          btn.textContent = 'Added!';
          btn.classList.add('is-added');
          setTimeout(function () {
            btn.textContent = 'Add to Cart';
            btn.classList.remove('is-added');
            btn.disabled = false;
          }, 2000);
        }).catch(function () {
          btn.textContent = 'Error';
          btn.disabled = false;
          setTimeout(function () { btn.textContent = 'Add to Cart'; }, 2000);
        });
      });
    });
  }

  /* ── sync all heart buttons on page ── */
  function syncHeartButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');
      var inList = isInWishlist(pid);
      btn.classList.toggle('is-wishlisted', inList);
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  /* ── badge counter ───────────────────────────────────── */
  function updateBadges() {
    var count = getCount();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ── init ─────────────────────────────────────────────── */
  function init() {
    initHeartButtons();
    updateBadges();

    // Drawer close button
    var drawer = getDrawer();
    if (drawer) {
      var closeBtn = drawer.querySelector('.pbp-wishlist-drawer__close');
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    }
    if (overlayEl) overlayEl.addEventListener('click', closeDrawer);

    // Escape key closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // Listen for changes to update badges
    document.addEventListener('pbp:wishlist:change', function () {
      updateBadges();
    });

    // Expose open/close for external use
    window.PBPWishlist.openDrawer = openDrawer;
    window.PBPWishlist.closeDrawer = closeDrawer;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
